import { Button, Fade, Typography } from 'components/common'
import { useCollectiveDemoContext } from 'context/CollectiveDemoContext'
import { CollectiveProposalStatus, VoteType } from 'graphql/generated'
import { useDemoVote } from 'hooks/collectives/demo/useDemoVote'
import { useExecuteProposal } from 'hooks/collectives/demo/useExecuteProposal'
import { useSignProposal } from 'hooks/collectives/demo/useSignProposal'
import { useSucceedProposal } from 'hooks/collectives/demo/useSucceedProposal'
import { useUpdateCollectiveMembership } from 'hooks/collectives/demo/useUpdateCollectiveMembership'
import { useMemo } from 'react'

type StepDetailType = {
  showButton?: boolean
  description?: React.ReactNode
  buttonLabel?: string
  buttonCallback?: Callback
}

export const DemoDescriptionSteps: React.FC<{
  goNextStep: Callback
}> = ({ goNextStep }) => {
  const {
    currentStep,
    totalStepsCount,
    collectiveDemo,
    demoActiveProposal,
    setDemoActiveProposal,
    goToStep,
    possibleMembers,
    setViewingProposal
  } = useCollectiveDemoContext()

  const demoVote = useDemoVote()
  const succeedProposal = useSucceedProposal()
  const signProposal = useSignProposal()
  const executeProposal = useExecuteProposal()
  const updateCollectiveMembership = useUpdateCollectiveMembership()

  const stepDetail: StepDetailType = useMemo(() => {
    if (!collectiveDemo) {
      return {}
    }

    if (currentStep === 0) {
      if (!collectiveDemo.membershipRequest) {
        return {
          showButton: false,
          description: (
            <>Connect your crypto wallet and submit a request to join the DAO</>
          )
        }
      } else {
        return {
          showButton: true,
          buttonLabel: 'Next Step',
          description: (
            <>
              Your request to join the DAO will be reviewed by existing DAO
              members.
              <br />
              <br />
              Let’s assume you’ve been accepted. Now, you’ll contribute ETH to
              be able to create and vote on proposals.
            </>
          ),
          buttonCallback: () => {
            if (!demoActiveProposal) {
              return
            }

            demoVote(demoActiveProposal.id, VoteType.FOR, possibleMembers[3])
            succeedProposal(demoActiveProposal.id)

            signProposal(demoActiveProposal.id, possibleMembers[3])
            executeProposal(demoActiveProposal.id, possibleMembers[4])
            setDemoActiveProposal(undefined)

            goNextStep()
          }
        }
      }
    } else if (currentStep === 1) {
      if (
        !collectiveDemo.membership?.totalTokens ||
        collectiveDemo.membership?.totalTokens === '0'
      ) {
        return {
          showButton: false,
          description: (
            <>
              Once approved, you can contribute ETH.
              <br />
              <br />
              When you contribute ETH, you receive the DAO’s tokens. Tokens are
              used to vote. The more tokens you have, the more voting power.
            </>
          )
        }
      } else if (collectiveDemo.membership.totalTokens !== '0') {
        return {
          showButton: true,
          buttonLabel: 'Next',
          description: (
            <>You’ve successfully contributed ETH! Now to create a proposal.</>
          ),
          buttonCallback: () => {
            goNextStep()
          }
        }
      }
    } else if (currentStep === 2) {
      if (!demoActiveProposal) {
        return {
          showButton: false,
          description: (
            <>
              Use proposals to suggest actions the DAO should take.
              <br />
              <br />
              Let’s run through an example. Your friend wants to join the DAO.
              Create a proposal to propose them as a new member.
            </>
          )
        }
      } else if (demoActiveProposal) {
        goToStep(3)
      }
    } else if (currentStep === 3) {
      if (!demoActiveProposal?.myVote) {
        return {
          showButton: false,
          description: (
            <>You successfully created a proposal. Now, go cast your vote.</>
          )
        }
      } else if (
        demoActiveProposal.myVote &&
        demoActiveProposal.status !== CollectiveProposalStatus.SUCCEEDED &&
        demoActiveProposal.status !== CollectiveProposalStatus.DEFEATED &&
        demoActiveProposal.status !== CollectiveProposalStatus.EXECUTED
      ) {
        if (
          demoActiveProposal.myVote === VoteType.AGAINST ||
          demoActiveProposal.myVote === VoteType.ABSTAIN
        ) {
          return {
            showButton: false,
            description: (
              <>
                Oh No, seems the proposal will fail. Please reload tutorial to
                continue with demo
              </>
            )
          }
        } else {
          return {
            showButton: true,
            description: (
              <>
                A minimum number of voters need to participate on each proposal.
                This is called quorum and is defaulted to 30%.
                <br />
                <br />
                Once a vote passes, it needs to be executed by signators. When
                they sign the proposal, it is executed on the blockchain.
                <br />
                <br />
                We’ll show you the view as a signator now.
                <br />
              </>
            ),
            buttonLabel: 'View as Signator',
            buttonCallback: () => {
              if (!demoActiveProposal || !collectiveDemo) {
                return
              }

              succeedProposal(demoActiveProposal.id)
              signProposal(demoActiveProposal.id, possibleMembers[0])
              collectiveDemo.membership = possibleMembers[0].membership
              updateCollectiveMembership(true, true)
              goNextStep()
            }
          }
        }
      }
    } else if (currentStep === 4) {
      if (!demoActiveProposal) {
        return {
          description: <>Uh Oh, something went wrong. please reset the demo</>
        }
      } else if (
        demoActiveProposal.status === CollectiveProposalStatus.SUCCEEDED
      ) {
        return {
          showButton: false,
          description: (
            <>
              Once the vote has passed, it needs to be signed by the DAO&lsquo;s
              signators to be executed on the blockchain.
            </>
          )
        }
      } else if (
        demoActiveProposal.status === CollectiveProposalStatus.EXECUTED
      ) {
        return {
          showButton: true,
          description: (
            <>
              The proposal was successfully executed!
              <br />
              <br />
              Your friend is now in the DAO.
            </>
          ),
          buttonLabel: 'Continue',
          buttonCallback: () => {
            setViewingProposal(undefined)
            goNextStep()
          }
        }
      }
    } else if (currentStep === 5) {
      setTimeout(() => window.scrollTo({ top: 600, behavior: 'smooth' }), 500)

      return {
        showButton: true,
        buttonLabel: 'Continue',
        buttonCallback: () => {
          goNextStep()
        }
      }
    } else if (currentStep === totalStepsCount - 1) {
      return {
        showButton: false
      }
    }
    return {
      showButton: false
    }
  }, [
    collectiveDemo,
    currentStep,
    demoActiveProposal,
    demoVote,
    executeProposal,
    goNextStep,
    goToStep,
    possibleMembers,
    setDemoActiveProposal,
    setViewingProposal,
    signProposal,
    succeedProposal,
    totalStepsCount,
    updateCollectiveMembership
  ])

  return (
    <Fade in={stepDetail.showButton || !!stepDetail.description}>
      <div>
        {!!stepDetail.description && (
          <Typography fontWeight="light" size="sm" component={'span'}>
            {stepDetail.description}
          </Typography>
        )}

        {stepDetail.showButton && (
          <div className="mt-2 min-w-[100px] flex justify-start">
            <Button
              size="sm"
              label={stepDetail.buttonLabel || 'Continue'}
              color="blue"
              onClick={() => {
                stepDetail.buttonCallback?.()
              }}
            />
          </div>
        )}
      </div>
    </Fade>
  )
}
